import { GeoTIFFImage } from 'geotiff';
import { fromUrl } from 'geotiff';
import { Auth, IAuthToken } from '@shared/Auth';
import { RealityCapturePoint } from '@typings/reality-capture/common';
import { Vector3 } from '@math.gl/core';

export function uuidtoUrl(uuid: string): string {
    if (kuura.getServerDomain() === "dev.infrakit.info") {
        return `https://tiles.devmaps.infrakit.com/v1/proj/${kuura.getProjectUuid()}/doc/${uuid}/1/dem.tif`;
    }
    return `https://tiles.maps.infrakit.com/v1/proj/${kuura.getProjectUuid()}/doc/${uuid}/1/dem.tif`;
}

export async function getRasterImage(url: string): Promise<GeoTIFFImage> {
    const token: IAuthToken = await Auth.getToken();
    const tiff = await fromUrl(url, {
        headers: {
            Authorization: `Bearer ${token.accessToken}`
        }
    });
    return await tiff.getImage();
}

export function downsamplePoints(points: RealityCapturePoint[], maxPoints: number = 150): RealityCapturePoint[] {
    // Downsample if we have too many points
    let downsampledPoints = points;
    if (points.length > maxPoints) {
        const step = Math.ceil(points.length / maxPoints);
        downsampledPoints = points.filter((_, index) => index % step === 0);
    }
    return downsampledPoints;
}

export function filterPoints(points: RealityCapturePoint[], startVector: Vector3, endVector: Vector3, rectangleWidth: number): RealityCapturePoint[] {
    const filteredPoints = points.filter(p => {
    const l2 = (startVector.x - endVector.x) ** 2 + (startVector.y - endVector.y) ** 2;
    if (l2 === 0) return false;
        let t = ((p.point.x - startVector.x) * (endVector.x - startVector.x) + (p.point.y - startVector.y) * (endVector.y - startVector.y)) / l2;
        t = Math.max(0, Math.min(1, t));
        const distSq = (p.point.x - (startVector.x + t * (endVector.x - startVector.x))) ** 2 + (p.point.y - (startVector.y + t * (endVector.y - startVector.y))) ** 2;
        return distSq <= (rectangleWidth / 2) ** 2;
    });
    return filteredPoints;
}

/**
 * Calculates the pixel window [minX, minY, maxX, maxY] from a bounding box.
 */
export function getWindowFromBBox(image: GeoTIFFImage, bbox: number[]): [number, number, number, number] {
    const [oX, oY] = image.getOrigin();
    const [rX, rY] = image.getResolution();
    const imageBBox = image.getBoundingBox();

    const [minImageX, minImageY, maxImageX, maxImageY] = imageBBox;
    const [minX, minY, maxX, maxY] = bbox;
    
    // Clamp bbox to image bounds
    const clampedMinX = Math.max(minX, minImageX);
    const clampedMinY = Math.max(minY, minImageY);
    const clampedMaxX = Math.min(maxX, maxImageX);
    const clampedMaxY = Math.min(maxY, maxImageY);

    // Calculate pixel coordinates for the bbox corners
    const x1 = (clampedMinX - oX) / rX;
    const y1 = (clampedMinY - oY) / rY;
    const x2 = (clampedMaxX - oX) / rX;
    const y2 = (clampedMaxY - oY) / rY;

    const wMinX = Math.min(x1, x2);
    const wMaxX = Math.max(x1, x2);
    const wMinY = Math.min(y1, y2);
    const wMaxY = Math.max(y1, y2);

    const window = [Math.floor(wMinX), Math.floor(wMinY), Math.ceil(wMaxX), Math.ceil(wMaxY)];

    console.log('getWindowFromBBox', window);

    // Round to integer pixel bounds (floor min, ceil max to cover the area)
    return window as [number, number, number, number];
}

/**
 * Converts a raster array to a list of points with world coordinates.
 */
export async function rasterToPoints(
    name: string,
    rasters: any,
    image: GeoTIFFImage,
    window: [number, number, number, number],
    sourceIndex: number
): Promise<RealityCapturePoint[]> {
    // We assume band 0 is elevation
    const data = rasters[0];

    const width = rasters.width;
    const height = rasters.height;

    // Use the window coordinates for world coordinate mapping
    const [wMinX, wMinY] = window;

    const origin = image.getOrigin();
    const resolution = image.getResolution();
    const originX = origin[0];
    const originY = origin[1];
    const resX = resolution[0];
    const resY = resolution[1]; // Typically negative for map images

    const points: RealityCapturePoint[] = [];

    // Iterate through the fetched raster data
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const value = data[y * width + x];
            // Skip nodata pixels
            if (!isFinite(value)) continue;
            const worldX = originX + (wMinX + x + 0.5) * resX;
            const worldY = originY + (wMinY + y + 0.5) * resY;
            const point: RealityCapturePoint = {
                name: name,
                point: new Vector3(worldX, worldY, value),
                color: [1, 1, 1], // Default white color for DEM points
                sourceFileId: sourceIndex // Use index as ID for now
            };
            points.push(point);
        }
    }

    return points;
}